<template>
  <div>
    <Breadcrumb
      :items="breadcrumb"
      with-brodos
    />

    <main data-page-type="shop">
      <div class="grid">
        <div class="grid__item grid__item--12">
          <Heading
            class="shop-list__headline text--center"
            spaces-bottom-rem
            spaces-top-rem
          >
            <span v-if="searchTerm">
              Deine Suche nach <strong>{{ truncatedSearchTerm }}</strong>
            </span>
            <span v-else>{{ pageHeadline }}</span>
          </Heading>
        </div>
      </div>

      <div
        v-if="displayFiltersAndCount"
        class="shop-list__results-count text--center"
        data-nosnippet
      >
        {{ offersCount }} <template v-if="offersCount === 1">Produkt</template
        ><template v-else>Produkte</template>
      </div>

      <FilterAccordion
        v-if="displayFiltersAndCount"
        :aggregations="aggregations"
        :current-offers-count="offersCount"
        :item-urls="itemUrls"
        :variables="listViewParams"
        :hidden-filters="hiddenFilters"
        @submit="refreshOffers"
      />

      <GridContainer>
        <OfferTilesContainer
          v-if="hasOffers && offers.entries"
          :offers="offers.entries"
          class="shop-list__offer-tiles"
        />
        <div
          v-else
          class="shop-list__empty-result text--center"
        >
          <template v-if="status === 'pending'">Bitte warten...</template>
          <template v-else>Es wurden leider keine Produkte gefunden.</template>
        </div>

        <div
          v-if="showPagination"
          class="shop-list__pagination-container"
        >
          <Pagination
            class="shop-list__pagination"
            :current-page="pagination.page"
            :total-pages="pagination.totalPages"
            :url="pageUrl"
          />
          <PageLengthSelector
            class="shop-list__page-length"
            :current-page-length="pagination.size"
            @change="pageLengthChanged($event)"
          />
        </div>
      </GridContainer>

      <ShopListSeoContent
        :page-headline="pageHeadline"
        :route-slug="$route.params?.config || ''"
      />
    </main>
  </div>
</template>

<script lang="ts" setup>
import { setResponseHeader, setResponseStatus } from 'h3';
import { ref, computed, watch, onBeforeUnmount, type Ref } from 'vue';
import {
  defineNuxtComponent,
  useLazyAsyncData,
  useNuxtApp,
  useRoute,
  useRouter,
  onMounted,
  useHead,
} from '#imports';
import type { SafeLink } from '@unhead/schema';
import { truncate } from 'lodash-es';

import { checkCategoryFilters, getRedirectPath } from '~/lib/handle-list-filter-responses';
import calculateLastModified from '~/lib/last-modified-offer';
import fetchOffers from '~/lib/goliath/offers';
import mergeFilterVariables from '~/lib/filter-params/merge-variables';

import { GridContainer, Heading } from '@i22/td-longtail-vue-components';

import Breadcrumb from '~/components/breadcrumb.vue';
import FilterAccordion from '~/components/filter/filter-accordion.vue';
import OfferTilesContainer from '~/components/offer-tiles-container.vue';
import PageLengthSelector from '~/components/page-length-selector.vue';
import Pagination from '~/components/pagination.vue';
import ShopListSeoContent from '~/components/shop-list-seo-content.vue';
import type { ListQuery } from '~/lib/goliath/goliath.types';
import { TrackAsync } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import { getMappedCategories, getSeoTemplateContent } from '~/lib/seo/seo-template';
import { isListPageIndexed } from '~/lib/page-index-helper';
import { useNavigationStore } from '~/stores/navigation';
import type { FilterQuery } from '~/components/filter/filter-types';
import { parseFilterParams, serializeFilterParams } from '~/lib/filter-params';

defineNuxtComponent({
  name: 'ShopLlist',
});

const nuxtApp = useNuxtApp();
const $router = useRouter();
const $route = useRoute();

const listViewParams = ref<FilterQuery>({});
const paginationSize = computed(() =>
  listViewParams.value.pagination ? listViewParams.value.pagination.size : ''
);

const itemUrl = (newVariables = {}) => {
  const variables = mergeFilterVariables(listViewParams.value, newVariables);
  const options = serializeFilterParams(variables);
  return $router.resolve(options).href;
};

const pageUrl = (currentPage) => {
  let page = currentPage;
  if (!page || page <= 1) page = null;

  return itemUrl({
    pagination: {
      page,
      size: listViewParams.value.isPaginationDefault ? null : paginationSize.value,
    },
  });
};

watch(
  () => $route.fullPath,
  () => {
    const parsedRouteParams = parseFilterParams($route);
    if (!parsedRouteParams) return;
    listViewParams.value = parsedRouteParams;
    listViewParams.value.grouped = true;
    if (!listViewParams.value.filter) {
      listViewParams.value.filter = {};
    }
    listViewParams.value.filter.listed = true;
    // All products should be displayed, so we disable the available filter.
    // listViewParams.value.filter.productAvailable = true; // show only products with an offer (available(
    // listViewParams.value.filter.hasAlwaysDisplayed = true; // AND show products without an offer, when they have the flag "always_display"

    if ($route.query?.articleNumbers && typeof $route.query.articleNumbers === 'string') {
      listViewParams.value.filter.articleNumbers = $route.query.articleNumbers.split(',');
    }
  },
  { immediate: true }
);

const event = nuxtApp.ssrContext?.event;

const {
  data: offers,
  error: errorData,
  status,
}: { data: Ref<ListQuery['offers'] | undefined> } = useLazyAsyncData<
  ListQuery['offers'] | undefined
>(
  JSON.stringify(listViewParams.value),
  async (): Promise<ListQuery['offers'] | null> => {
    const goliathData =
      Object.keys(listViewParams.value).length > 0
        ? await fetchOffers(listViewParams.value, true)
        : undefined;
    // check unused categories from url and remove them to prevent a deadlock.
    const updatedUrl = checkCategoryFilters(listViewParams.value, goliathData);
    if (updatedUrl) {
      return nuxtApp.nuxt2Context.redirect(updatedUrl);
    }

    if (
      process.server &&
      // check is response is empty or 404
      (!goliathData ||
        (goliathData.pagination?.totalCount || 0) === 0 ||
        goliathData.code === 404 ||
        (goliathData.entries?.length || 0) === 0)
    ) {
      // We only want a redirect, when we have a valid URL.
      if (listViewParams.value !== undefined && Object.keys(listViewParams.value).length > 0) {
        const navigationStore = useNavigationStore();
        // Redirect to upper category if no offers found
        const redirectUrl = getRedirectPath(
          listViewParams.value.filter,
          navigationStore.navigationItems || []
        );
        if ($route.path !== redirectUrl) {
          return nuxtApp.nuxt2Context.redirect(redirectUrl);
        }
      }

      // if there redirected page is the same, send a 404
      setResponseStatus(event, 404);
    }

    if (!goliathData || !goliathData?.entries) return null;

    if (process.server && goliathData && !nuxtApp.nuxt2Context.res.headersSent) {
      setResponseHeader(
        event,
        'last-modified',
        calculateLastModified(goliathData, new Date(nuxtApp.$config.public.buildDate))
      );
    }
    return goliathData;
  },
  {
    watch: [listViewParams],
  }
);

// show loading spinner on change.
watch(
  () => status.value,
  () => {
    if (status.value === 'pending') {
      // "page:loading:start" does show the Spinner, but it will disappear too early. So we use a custom Hook!
      nuxtApp.hooks.callHook('egs:loading:start');
    } else {
      nuxtApp.hooks.callHook('egs:loading:end');
    }
  }
);

onBeforeUnmount(() => {
  nuxtApp.hooks.callHook('egs:loading:end');
});

// handle errors
if (errorData.value) {
  // Show error page
  throw nuxtApp.nuxt2Context.error(errorData.value);
}

const seoTemplate = computed(() => getSeoTemplateContent(listViewParams.value, offers.value));

getHeader(
  {
    title: computed(() => seoTemplate.value.title),
    description: computed(() => seoTemplate.value.description),
    isIndexed: computed(() => isListPageIndexed(listViewParams.value, offers.value)),
  },
  {
    showCanonical: true,
  }
);

useHead(() => {
  const pagination = offers.value?.pagination;

  if (!pagination?.page || !pagination?.totalPages) return {};

  const safeLink: Partial<SafeLink>[] = [];
  const prevPage = pagination.page - 1;
  const prevPagePath = prevPage < 1 ? undefined : pageUrl(prevPage);
  if (prevPagePath) {
    safeLink.push({
      rel: 'prev',
      href: new URL(prevPagePath, nuxtApp.$config.public.baseUrl).href,
    });
  }
  const nextPage = pagination.page + 1;

  const nextPagePath = nextPage > pagination.totalPages ? undefined : pageUrl(nextPage);
  if (nextPagePath) {
    safeLink.push({
      rel: 'next',
      href: new URL(nextPagePath, nuxtApp.$config.public.baseUrl).href,
    });
  }
  return { link: safeLink };
});

// When we are in csr, the "watch" for tracking is triggered before the page is mounted.
// An "immediate" would trigger the "old" content
const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});

watch(
  () => [offers.value, status.value, isMounted.value],
  () => {
    if (
      isMounted.value !== true ||
      offers.value === null ||
      !['idle', 'success'].includes(status.value)
    ) {
      return;
    }

    TrackAsync(
      {
        page_type: 'product.catalog',
      },
      { delay: 100 }
    );
  }
);

const aggregations = computed(() => offers.value?.aggregations || {});

const searchTerm = computed(() => listViewParams.value.search);
const truncatedSearchTerm = computed(() => {
  if (!searchTerm.value) return undefined;
  const defuzzedSearchTerm = searchTerm.value?.replace(/\* /g, ' ').replace(/\*$/, '');
  return truncate(defuzzedSearchTerm, { length: 30, omission: '…' });
});

const pageHeadline = computed(() => seoTemplate.value.pageHeadline);

const breadcrumb = computed(() => {
  const items = [];

  const { selectedBrands, selectedProductCategories, ancestorProductCategories } =
    getMappedCategories(listViewParams.value, offers.value);

  ancestorProductCategories.forEach((productCategory) => {
    items.push({
      label: productCategory?.name,
      to: serializeFilterParams({
        filter: { productCategoryPaths: [productCategory?.path] },
      } as FilterQuery),
    });
  });

  const productCategoryPaths = selectedProductCategories
    .map((category) => category.path)
    .filter(Boolean);

  if (selectedProductCategories.length) {
    const selectedProductCategoriesNames = selectedProductCategories.map(
      (category) => category.name
    );

    let label;
    if (selectedProductCategories.length > 2) {
      label = `${selectedProductCategoriesNames[0]}); ${selectedProductCategoriesNames[1]}, …`;
    } else {
      label = selectedProductCategoriesNames.join(', ');
    }

    items.push({
      label,
      to: serializeFilterParams({ filter: { productCategoryPaths } } as FilterQuery),
    });
  }

  if (selectedBrands.length) {
    const selectedBrandNames = selectedBrands.map((brand) => brand?.name).filter(Boolean);
    let label;
    if (selectedBrands.length > 2) {
      label = `${selectedBrandNames[0]}, ${selectedBrandNames[1]}, …`;
    } else {
      label = selectedBrandNames.join(', ');
    }
    const slugs = selectedBrands.map((brand) => brand?.slug).filter(Boolean);
    items.push({
      label,
      to: serializeFilterParams({
        filter: { productCategoryPaths, brandSlugs: slugs },
      } as FilterQuery),
    });
  }

  if (searchTerm.value) {
    items.push({ label: `Suche nach: ${truncatedSearchTerm.value}` });
  }

  return items;
});

const pagination = computed(() => offers.value?.pagination);

const offersCount = computed(() => offers.value?.variantsCount || 0);

const hasOffers = computed(() => offersCount.value > 0);

const showPagination = computed(() => pagination.value?.totalPages > 1);

const hasFilters = computed(() => {
  if (!listViewParams.value?.filter) return false;
  const defaultFilterKeys = new Set(['listed', 'productAvailable', 'hasAlwaysDisplayed']);
  const hasNonDefaultFilter = Object.keys(listViewParams.value?.filter).filter(
    (key) => !defaultFilterKeys.has(key)
  );
  return hasNonDefaultFilter.length > 0;
});

const displayFiltersAndCount = computed(() => {
  if (hasFilters.value) return true;
  return hasOffers.value;
});

const productCategoryFilterUrl = (path) =>
  itemUrl({
    filter: { productCategoryPaths: [path] },
    pagination: { page: null, size: null },
  });
const brandFilterUrl = (slug) =>
  itemUrl({
    filter: { brandSlugs: [slug] },
    pagination: { page: null, size: null },
  });
const colorFilterUrl = (slug) =>
  itemUrl({
    filter: { colorSlugs: [slug] },
    pagination: { page: null, size: null },
  });
const compatibleProductFamilyFilterUrl = (slug) =>
  itemUrl({
    filter: { compatibleProductFamilySlugs: [slug] },
    pagination: { page: null, size: null },
  });
const productTagSlugsFilterUrl = (slug) =>
  itemUrl({
    filter: { productTagSlugs: [slug] },
    pagination: { page: null, size: null },
  });

const itemUrls = computed(() => ({
  productCategories: productCategoryFilterUrl,
  brands: brandFilterUrl,
  colors: colorFilterUrl,
  compatibleProductFamilies: compatibleProductFamilyFilterUrl,
  productTagSlugs: productTagSlugsFilterUrl,
}));

const mainCategory = computed(() => $route.path.split('/')[1]);

const hiddenFilters = computed(() => {
  const paths = listViewParams.value.filter?.productCategoryPaths || [];
  if (
    paths.includes('smartphones-und-handys') ||
    paths.includes('smartphones-und-handys/smartphones') ||
    paths.includes('tablets-und-zubehoer/tablets') ||
    paths.includes('tablets-und-zubehoer')
  ) {
    return ['compatibleProductFamilies'];
  }
  return [];
});

// This is needed as async data does not refetch data on query-only change
/* created() {
    if (process.client) {
      // When we have "%2B" (urlencoded "+") in the URL (from the server), the asyncData isn't called when the customer changes some filter. So we replace the URL.
      const replaced = this.$route.query?.wasReplaced;
      const replacedPath = this.$route.path.replace('%2B', '+');
      const navigate = (routeOptions) => $router.replace(routeOptions).catch(() => {});
      if (replacedPath !== this.$route.path && !replaced) {
        navigate({
          query: { ...this.$route.query, wasReplaced: true },
          path: replacedPath,
        });
      }
      // remove "replaced" query from URL when no encoded sign is present.
      else if (!this.$route.path.includes('%2B') && replaced) {
        const { wasReplaced, ...remainingQuery } = this.$route.query;
        navigate({
          query: remainingQuery,
          path: this.$route.path,
        });
      }
    }
  }, */

const refreshOffers = (newVariables) => {
  const variablesWithPageSize = mergeFilterVariables(
    {
      pagination: {
        size: listViewParams.value.isPaginationDefault ? null : paginationSize.value,
        page: 1,
      },
    },
    newVariables
  );
  $router.push(serializeFilterParams(variablesWithPageSize));
};

const pageLengthChanged = (size) => {
  const variables = mergeFilterVariables(listViewParams.value, {
    pagination: { size, page: 1 },
  });

  $router.push(serializeFilterParams(variables));
};

defineExpose({ mainCategory, listViewParams, aggregations, pageUrl, itemUrls, offers, breadcrumb });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.shop-list {
  &__headline {
    position: relative;
    margin: {
      top: $grid-medium-gutter;
      bottom: $grid-small-gutter;
    }
  }

  &__results-count {
    font-size: 1.75rem;
    margin-bottom: $grid-medium-gutter;
    position: relative;
  }

  &__empty-result {
    margin-block: $grid-medium-gutter;
  }

  &__results {
    display: flex;
    overflow-x: hidden;
  }

  &__pagination-container {
    padding-inline: $grid-base;
    grid-gap: $grid-base * 2;
    margin-block: $grid-base * 2.5;
    display: flex;
    flex-direction: column;

    @include mq($mq-md) {
      display: grid;
      grid-template-columns: 195px auto 195px;
      margin-bottom: $grid-base * 4;
    }
  }

  &__pagination {
    grid-column: 2;
  }

  &__page-length {
    text-align: center;
    align-self: end;
    grid-column: 3;
  }

  &__promo-tile-badge {
    position: absolute;
    top: 40%;
    right: 2%;
    transform: scale(0.7);
    @include mq(480px) {
      transition: all ease 500ms;
      transform: scale(1);
    }
    @include mq($mq-sm) {
      top: 5%;
      left: 5%;
    }
    @include mq(800px) {
      left: 15%;
    }
    @include mq($mq-image-large) {
      top: 5%;
      left: 25%;
    }
    @include mq($mq-xl) {
      top: 8%;
      left: 30%;
    }
  }

  &__offer-tiles {
    position: relative;
    margin-bottom: $grid-base * 2;
  }

  &__seo {
    margin-block: $grid-base * 2;

    @include mq($mq-sm) {
      margin-block: $grid-base * 4;
    }
  }
}
</style>
